/**
 * Zone 6: Ultimate Challenge Decorator
 *
 * Handles decoration for the Ultimate Challenge zone (Zone 6)
 * Includes 6 decoration types: 4 top-side + 2 underside
 *
 * Top decorations: energy-orb, rainbow-shard, cosmic-particle, portal-fragment
 * Underside decorations: lightning-bolt, energy-tendril
 *
 * PHASE 7C: Converted from Graphics to Sprites
 */

import Phaser from 'phaser';
import { PlatformDecoratorBase, DecorationConfig } from './PlatformDecoratorBase';
import { DecorationTextureGenerator } from '../DecorationTextureGenerator'; // PHASE 7C

export class Zone6Decorator extends PlatformDecoratorBase {
  protected getDecorations(): DecorationConfig[] {
    return [
      // Top-side decorations
      {
        type: 'energy-orb',
        chance: 0.22,
        minWidth: 50,
        placement: 'top',
      },
      {
        type: 'rainbow-shard',
        chance: 0.18,
        minWidth: 55,
        placement: 'top',
      },
      {
        type: 'cosmic-particle',
        chance: 0.28,
        minWidth: 40,
        placement: 'top',
      },
      {
        type: 'portal-fragment',
        chance: 0.12,
        minWidth: 70,
        placement: 'top',
      },
      // Underside decorations
      {
        type: 'lightning-bolt',
        chance: 0.25,
        minWidth: 50,
        placement: 'underside',
      },
      {
        type: 'energy-tendril',
        chance: 0.3,
        minWidth: 60,
        placement: 'underside',
      },
    ];
  }

  /**
   * Add a specific decoration to the platform
   * PHASE 7C: CONVERTED TO SPRITES (was Graphics)
   */
  protected addDecoration(
    platformContainer: Phaser.GameObjects.Container,
    decorationType: string,
    _placement: 'top' | 'underside',
    width: number,
    height: number
  ): void {
    // PHASE 7C: Create sprite from pre-generated texture
    const textureKey = DecorationTextureGenerator.getTextureKey(decorationType);
    const sprite = this.scene.add.sprite(0, 0, textureKey);
    sprite.setOrigin(0.5, 0.5);
    platformContainer.add(sprite);

    const position = this.calculateDecorationPosition(decorationType, width, height);
    sprite.setPosition(position.x, position.y);

    const animationType = this.getAnimationType(decorationType);
    if (animationType) {
      this.animatedElements.push({
        sprite,
        type: animationType,
        animationPhase: this.random.nextFloat(0, Math.PI * 2),
        animationSpeed: this.getAnimationSpeed(animationType),
      });
    }
  }

  private calculateDecorationPosition(
    type: string,
    width: number,
    height: number
  ): { x: number; y: number } {
    // CRITICAL FIX: Only use platform-dependent positioning (constant offsets in texture)
    switch (type) {
      case 'energy-orb':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: -height / 2, // Texture has the random -10 to -20 offset
        };
      case 'rainbow-shard':
        return {
          x: this.random.nextFloat(-width * 0.25, width * 0.25),
          y: -height / 2, // Texture has the -8 offset
        };
      case 'cosmic-particle':
        return {
          x: this.random.nextFloat(-width * 0.4, width * 0.4),
          y: -height / 2, // Texture has the random -8 to -20 offset
        };
      case 'portal-fragment':
        return {
          x: this.random.nextFloat(-width * 0.15, width * 0.15),
          y: -height / 2, // Texture has the -12 offset
        };
      case 'lightning-bolt':
        return {
          x: this.random.nextFloat(-width * 0.3, width * 0.3),
          y: height / 2,
        };
      case 'energy-tendril':
        return {
          x: this.random.nextFloat(-width * 0.25, width * 0.25),
          y: height / 2,
        };
      default:
        return { x: 0, y: 0 };
    }
  }

  private getAnimationType(type: string): string | null {
    switch (type) {
      case 'energy-orb':
        return 'energy-pulse';
      case 'energy-tendril':
        return 'energy-wave';
      default:
        return null;
    }
  }

  private getAnimationSpeed(type: string): number {
    switch (type) {
      case 'energy-pulse':
        return 0.1;
      case 'energy-wave':
        return 0.08;
      default:
        return 0.05;
    }
  }
}
